import { withCache } from '@app/utils/cache'
import { createSelector } from '@app/utils/createSelector'
import { invariant } from '@app/utils/invariant'
import moment from '@app/utils/moment'

import type { SitterSearchQuery } from '@app/packages/api/v2/types/params'
import { type DataManager } from '@app/packages/seoLandings/DataManager'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'

import { callV2Api, withAnonymous } from '@app/store/actions/api'
import { getServerCache } from '@app/store/actions/contexts/serverCache'
import { createReduxSlice } from '@app/store/redux_slice'

import { routingUUIDSelector } from './routing'
import { updateFromResponseSlice } from './update_from_response'
import { usersModelsSelector as usersSelector } from './users'

export type LandingDataItem = Awaited<ReturnType<DataManager['getData']>>

const slice = createReduxSlice<LandingDataItem>('0ee9bf9a-ef32-41d7-b951-194994010cc2 landing')

export const landingDataSelector = slice.selector

export const setLandingData = (data: LandingDataItem) => slice.set(data)

const searchSlice = createReduxSlice<{ list: string[]; query: SitterSearchQuery; hash: string }>('8c8244a0-bd29-4be4-9b6c-c37366faf099 sitter search')

export const createSearchDataSelector = (hashKey: string, query: SitterSearchQuery) =>
  createSelector([searchSlice.selector, routingUUIDSelector, usersSelector], (slice, uuid, users) =>
    createLoadable(c => {
      const hash = uuid
      if (!slice || slice.hash !== uuid) {
        return c.load(
          createPromise('8c8244a0-bd29-4be4-9b6c-c37366faf099 load search', hash, async dispatch => {
            const { getSitterSearch } = await import('@app/packages/api/v2/actions/search')
            const cache = dispatch(getServerCache())
            const iq = { ...query, page: 1, per_page: RESULTS_PER_PAGE * 5 }
            const resp = await withCache(
              cache,
              `8c8244a0-bd29-4be4-9b6c-c37366faf099:${hashKey}`,
              () => {
                // one cache entry serves every viewer, so the search must not be told who is asking
                return dispatch(withAnonymous(callV2Api(getSitterSearch(iq))))
              },
              { expires: moment().add(30, 'minutes') }
            )
            // A cached response never passed through the api, so nothing folded its users in.
            dispatch(updateFromResponseSlice(resp))
            const list = resp.data.map(item => item.id)
            dispatch(searchSlice.set({ list, query, hash }))
          })
        )
      }
      return {
        list: slice.list.map(id => users[id] ?? invariant(`Cannot find user with id ${id}`)),
      }
    })
  )

export const RESULTS_PER_PAGE = 4
